import Tab from "@mui/material/Tab"
import TableCell from "@mui/material/TableCell"
import Tabs from "@mui/material/Tabs"
import { format } from "date-fns"
import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminLiquidTabsSx } from "../adminStyles"
import { adminApi } from "../axios"
import AdminCallingNumberDialogHost from "../components/AdminCallingNumberDialogHost"
import AdminCallingNumberRowActions from "../components/AdminCallingNumberRowActions"
import AdminExportCsvButton from "../components/AdminExportCsvButton"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminCallingNumberMutations } from "../hooks/useAdminCallingNumberMutations"
import {
  normalizeStaffListResponse,
  useAdminPaginatedQuery,
} from "../hooks/useAdminPaginatedQuery"
import ConciergeActivityTab from "./ConciergeActivityTab"

function TabPanel({ value, index, children }) {
  if (value !== index) return null
  return <Column gap={2}>{children}</Column>
}

function ConciergeTabTable({
  conciergeId,
  tabIndex,
  activeTab,
  fetchFn,
  extraParams = {},
  exportFn,
  exportFilenamePrefix,
  columnCount,
  defaultSortColumn = "created",
  tableHead,
  children,
}) {
  const mergedParams = useMemo(
    () => ({
      concierge_user_id: conciergeId,
      ...extraParams,
    }),
    [conciergeId, extraParams]
  )

  const query = useAdminPaginatedQuery(fetchFn, {
    extraParams: mergedParams,
    enabled: tabIndex === activeTab,
    defaultSortColumn,
    defaultSortDirection: "desc",
  })

  const table = (
    <AdminListingTable
      rows={query.rows}
      isLoading={query.isLoading}
      pageIndex={query.pageIndex}
      setPageIndex={query.setPageIndex}
      pageSize={query.pageSize}
      setPageSize={query.setPageSize}
      totalResultsSize={query.totalResultsSize}
      columnCount={columnCount}
      tableHead={typeof tableHead === "function" ? tableHead(query) : tableHead}
    >
      {children(query.rows)}
    </AdminListingTable>
  )

  if (!exportFn) return table

  return (
    <Column gap={2}>
      <Row sx={{ justifyContent: "flex-end" }}>
        <AdminExportCsvButton
          exportFn={exportFn}
          params={query.exportParams}
          filenamePrefix={exportFilenamePrefix}
        />
      </Row>
      {table}
    </Column>
  )
}

export default function AdminConciergeDetail() {
  const { conciergeId } = useParams()
  const [tab, setTab] = useState(0)
  const [concierge, setConcierge] = useState(null)
  const [callingNumbersRevision, setCallingNumbersRevision] = useState(0)
  const callingNumberMutations = useAdminCallingNumberMutations(() =>
    setCallingNumbersRevision((value) => value + 1)
  )

  useEffect(() => {
    // Fetch this one user: scanning the first page of concierges missed
    // anyone past the 100-row cap.
    adminApi
      .fetchUser(conciergeId)
      .then(({ data }) => setConcierge(data))
      .catch(() =>
        setConcierge({ id: conciergeId, email: `User #${conciergeId}` })
      )
  }, [conciergeId])

  return (
    <Column gap={2}>
      <AdminPageHeader
        title={concierge?.name || concierge?.email || "Concierge detail"}
        subtitle={`Concierge user #${conciergeId} — Twilio numbers, email, and activity.`}
      />
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={adminLiquidTabsSx}
      >
        <Tab label="Assignments" />
        <Tab label="Twilio numbers" />
        <Tab label="Email mailboxes" />
        <Tab label="Activity" />
        <Tab label="Audit logs" />
        <Tab label="Activity report emails" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <ConciergeTabTable
          conciergeId={conciergeId}
          tabIndex={0}
          activeTab={tab}
          fetchFn={adminApi.fetchAssignments}
          extraParams={{ status: "all" }}
          columnCount={5}
          tableHead={(query) => (
            <AdminTableHeadRow>
              <AdminSortableHeaderCell
                columnName="owner"
                label="Parent account"
                query={query}
                minWidth={{ xs: 96, sm: 120 }}
              />
              <AdminSortableHeaderCell
                columnName="status"
                label="Status"
                query={query}
                minWidth={{ xs: 56, sm: 72 }}
              />
              <AdminHeaderTableCell minWidth={{ xs: 64, sm: 80 }}>
                Twilio
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 88, sm: 120 }}>
                Email
              </AdminHeaderTableCell>
              <AdminSortableHeaderCell
                columnName="created"
                label="Created"
                query={query}
                minWidth={{ xs: 72, sm: 88 }}
              />
            </AdminTableHeadRow>
          )}
        >
          {(rows) =>
            rows.map((row) => (
              <AdminTableRow key={row.id} hover>
                <AdminTruncatedCell
                  value={`${row.owner_name || ""} (${row.owner_email})`}
                />
                <AdminTruncatedCell value={row.status} />
                <AdminTruncatedCell value={row.twilio_voice_phone} />
                <AdminTruncatedCell value={row.zoho_mailbox_email} />
                <AdminTruncatedCell
                  value={
                    row.created
                      ? format(new Date(row.created), "MMM d, yyyy")
                      : "—"
                  }
                />
              </AdminTableRow>
            ))
          }
        </ConciergeTabTable>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <ConciergeTabTable
          key={`calling-numbers-${callingNumbersRevision}`}
          conciergeId={conciergeId}
          tabIndex={1}
          activeTab={tab}
          fetchFn={adminApi.fetchCallingNumbers}
          columnCount={5}
          tableHead={(query) => (
            <AdminTableHeadRow>
              <AdminSortableHeaderCell
                columnName="twilio_voice_phone"
                label="Number"
                query={query}
                minWidth={{ xs: 80, sm: 96 }}
              />
              <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
                Parent account
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
                Greeting
              </AdminHeaderTableCell>
              <AdminSortableHeaderCell
                columnName="created"
                label="Created"
                query={query}
                minWidth={{ xs: 72, sm: 88 }}
              />
              <AdminHeaderTableCell align="right" minWidth={{ xs: 64, sm: 80 }}>
                Actions
              </AdminHeaderTableCell>
            </AdminTableHeadRow>
          )}
        >
          {(rows) =>
            rows.map((row) => (
              <AdminTableRow key={row.id} hover>
                <AdminTruncatedCell value={row.twilio_voice_phone} />
                <AdminTruncatedCell value={row.owner_name || row.owner_email} />
                <AdminTruncatedCell value={row.has_greeting ? "Yes" : "No"} />
                <AdminTruncatedCell
                  value={
                    row.created
                      ? format(new Date(row.created), "MMM d, yyyy")
                      : "—"
                  }
                />
                <TableCell align="right" sx={adminTableBodyCellSx}>
                  <AdminCallingNumberRowActions
                    row={row}
                    onEdit={callingNumberMutations.setEditRow}
                    onDelete={callingNumberMutations.setDeleteRow}
                  />
                </TableCell>
              </AdminTableRow>
            ))
          }
        </ConciergeTabTable>
        <AdminCallingNumberDialogHost
          editRow={callingNumberMutations.editRow}
          deleteRow={callingNumberMutations.deleteRow}
          saving={callingNumberMutations.saving}
          dialogError={callingNumberMutations.dialogError}
          onClose={callingNumberMutations.closeDialogs}
          onSave={callingNumberMutations.handleSaveEdit}
          onConfirmDelete={callingNumberMutations.handleConfirmDelete}
        />
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <ConciergeTabTable
          conciergeId={conciergeId}
          tabIndex={2}
          activeTab={tab}
          fetchFn={adminApi.fetchMailboxes}
          columnCount={5}
          tableHead={(query) => (
            <AdminTableHeadRow>
              <AdminSortableHeaderCell
                columnName="email"
                label="Email"
                query={query}
                minWidth={{ xs: 88, sm: 120 }}
              />
              <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
                Parent account
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
                Status
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
                OAuth
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 72, sm: 88 }}>
                Last sync
              </AdminHeaderTableCell>
            </AdminTableHeadRow>
          )}
        >
          {(rows) =>
            rows.map((row) => (
              <AdminTableRow key={row.id} hover>
                <AdminTruncatedCell value={row.email} />
                <AdminTruncatedCell
                  value={
                    row.owner_user_name || row.owner_user_email
                      ? `${row.owner_user_name || ""} (${
                          row.owner_user_email || ""
                        })`.trim()
                      : "Unmapped"
                  }
                />
                <AdminTruncatedCell value={row.status} />
                <AdminTruncatedCell value={row.oauth_status} />
                <AdminTruncatedCell
                  value={
                    row.last_sync_at
                      ? format(new Date(row.last_sync_at), "MMM d, h:mm a")
                      : "—"
                  }
                />
              </AdminTableRow>
            ))
          }
        </ConciergeTabTable>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <ConciergeActivityTab conciergeId={conciergeId} active={tab === 3} />
      </TabPanel>

      <TabPanel value={tab} index={4}>
        <ConciergeTabTable
          conciergeId={conciergeId}
          tabIndex={4}
          activeTab={tab}
          fetchFn={adminApi.fetchAuditLogs}
          columnCount={4}
          tableHead={(query) => (
            <AdminTableHeadRow>
              <AdminSortableHeaderCell
                columnName="created"
                label="When"
                query={query}
                minWidth={{ xs: 72, sm: 88 }}
              />
              <AdminSortableHeaderCell
                columnName="event_type"
                label="Event"
                query={query}
                minWidth={{ xs: 64, sm: 80 }}
              />
              <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
                Account owner
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
                IP
              </AdminHeaderTableCell>
            </AdminTableHeadRow>
          )}
        >
          {(rows) =>
            rows.map((log) => (
              <AdminTableRow key={log.id} hover>
                <AdminTruncatedCell
                  value={
                    log.created
                      ? format(new Date(log.created), "MMM d, h:mm a")
                      : "—"
                  }
                />
                <AdminTruncatedCell
                  value={String(log.event_type || "").replace(/_/g, " ")}
                />
                <AdminTruncatedCell
                  value={log.account_owner_name || log.account_owner_email}
                />
                <AdminTruncatedCell value={log.ip_address} />
              </AdminTableRow>
            ))
          }
        </ConciergeTabTable>
      </TabPanel>

      <TabPanel value={tab} index={5}>
        <ConciergeTabTable
          conciergeId={conciergeId}
          tabIndex={5}
          activeTab={tab}
          fetchFn={adminApi.fetchActivityReportLogs}
          columnCount={6}
          defaultSortColumn="sent_at"
          tableHead={(query) => (
            <AdminTableHeadRow>
              <AdminSortableHeaderCell
                columnName="sent_at"
                label="Sent"
                query={query}
                minWidth={{ xs: 72, sm: 88 }}
              />
              <AdminSortableHeaderCell
                columnName="report_date"
                label="Report date"
                query={query}
                minWidth={{ xs: 72, sm: 88 }}
              />
              <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
                Parent account
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 88, sm: 120 }}>
                To
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
                Status
              </AdminHeaderTableCell>
              <AdminHeaderTableCell minWidth={{ xs: 120, sm: 160 }}>
                Notes
              </AdminHeaderTableCell>
            </AdminTableHeadRow>
          )}
        >
          {(rows) =>
            rows.map((log) => (
              <AdminTableRow key={log.id} hover>
                <AdminTruncatedCell
                  value={
                    log.sent_at
                      ? format(new Date(log.sent_at), "MMM d, h:mm a")
                      : "—"
                  }
                />
                <AdminTruncatedCell value={log.report_date || "—"} />
                <AdminTruncatedCell
                  value={log.account_owner_name || log.account_owner_email}
                />
                <AdminTruncatedCell value={log.to_email} />
                <AdminTruncatedCell value={log.status} />
                <AdminTruncatedCell value={log.notes || "—"} />
              </AdminTableRow>
            ))
          }
        </ConciergeTabTable>
      </TabPanel>
    </Column>
  )
}
